import { useState, useEffect } from 'react';
import {
  Snackbar,
  Button,
  Box,
  Typography,
  IconButton,
  Slide,
  Paper,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  PhoneIphone as PhoneIcon,
  Android as AndroidIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { isIOS, isAndroid } from '../utils/deviceDetection';
import { ANDROID_BRAND_COLORS, IOS_BRAND_COLORS } from '../constants/pwa';
import React from 'react';

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PWAInstallPrompt = () => {
  const { showInstallPrompt, handleInstall, dismissPrompt } = usePWAInstall();
  const [isDismissed, setIsDismissed] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);

  // Detect device type once on mount (device type doesn't change during session)
  useEffect(() => {
    setIsIOSDevice(isIOS());
    setIsAndroidDevice(isAndroid());
  }, []);

  const handleDismiss = (permanent = false) => {
    setIsDismissed(true);
    dismissPrompt(permanent);
    setMenuAnchor(null);
  };

  const handleInstallClick = async () => {
    const success = await handleInstall();
    if (success) {
      setIsDismissed(true);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Don't show anything if dismissed or shouldn't show
  if (!showInstallPrompt || isDismissed) {
    return null;
  }

  // Use the same custom Material-UI prompt for all devices (iOS and Android)
  return (
    <>
      <Snackbar
        open={true}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          zIndex: 2000,
          '@media (max-width: 599px)': {
            bottom: 16,
            left: 0,
            right: 0,
            transform: 'none !important'
          },
          '& .MuiSnackbarContent-root': {
            padding: 0,
            width: { xs: '100%', sm: 'auto' }
          }
        }}
        TransitionComponent={SlideTransition}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 1.5, sm: 2 },
            maxWidth: { xs: 'none', sm: 400 },
            mx: { xs: 1, sm: 0 },
            width: { xs: 'calc(100vw - 16px)', sm: 'auto' },
            borderRadius: 2,
            background: theme => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(8px)',
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            position: 'relative'
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            gap: { xs: 1.5, sm: 2 }
          }}>
            {/* Icon - Hidden on mobile */}
            <Box sx={{
              display: { xs: 'none', sm: 'flex' },
              p: 1,
              borderRadius: 1,
              backgroundColor: isAndroidDevice ? ANDROID_BRAND_COLORS.main : (isIOSDevice ? IOS_BRAND_COLORS.main : 'primary.main'),
              color: isAndroidDevice ? ANDROID_BRAND_COLORS.text : (isIOSDevice ? IOS_BRAND_COLORS.text : 'white'),
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {isAndroidDevice ? <AndroidIcon fontSize="small" /> : <PhoneIcon fontSize="small" />}
            </Box>

            <Box sx={{
              flex: 1,
              minWidth: 0,
              width: { xs: '100%', sm: 'auto' },
              pr: { xs: 5, sm: 0 }
            }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 0.5, sm: 1 }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Install CribCritic App
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Install CribCritic
                </Box>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.813rem', sm: '0.875rem' },
                  lineHeight: 1.4,
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {isIOSDevice
                    ? 'Get the native CribCritic app from the App Store for the best experience with offline access and push notifications.'
                    : 'Get quick access to CribCritic with our app. Install it for a better experience with offline access and push notifications.'
                  }
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {isIOSDevice
                    ? 'Get the native app for the best experience.'
                    : 'Get quick access with offline support.'
                  }
                </Box>
              </Typography>
              
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                width: '100%',
                mt: { xs: 0.5, sm: 0 }
              }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={isAndroidDevice ? <AndroidIcon /> : (isIOSDevice ? <PhoneIcon /> : <DownloadIcon />)}
                  onClick={handleInstallClick}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    minHeight: { xs: 44, sm: 36 },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    fontSize: { xs: '0.875rem', sm: '0.813rem' },
                    ...(isAndroidDevice && {
                      '&.MuiButton-root': {
                        backgroundColor: ANDROID_BRAND_COLORS.main,
                        backgroundImage: 'none',
                        color: ANDROID_BRAND_COLORS.text,
                        border: `1px solid ${ANDROID_BRAND_COLORS.main}`,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: ANDROID_BRAND_COLORS.hover,
                          backgroundImage: 'none',
                          boxShadow: 'none',
                        },
                        '&:active': {
                          backgroundColor: ANDROID_BRAND_COLORS.active,
                          backgroundImage: 'none',
                        }
                      }
                    }),
                    ...(isIOSDevice && {
                      '&.MuiButton-root': {
                        backgroundColor: IOS_BRAND_COLORS.main,
                        backgroundImage: 'none',
                        color: IOS_BRAND_COLORS.text,
                        border: `1px solid ${IOS_BRAND_COLORS.main}`,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: IOS_BRAND_COLORS.hover,
                          backgroundImage: 'none',
                          boxShadow: 'none',
                        },
                        '&:active': {
                          backgroundColor: IOS_BRAND_COLORS.active,
                          backgroundImage: 'none',
                        }
                      }
                    })
                  }}
                >
                  {isAndroidDevice ? 'Install App' : (isIOSDevice ? 'Get App' : 'Install')}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleDismiss(false)}
                  sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    minHeight: { xs: 44, sm: 36 },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    fontSize: { xs: '0.875rem', sm: '0.813rem' }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Maybe later
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Not now
                  </Box>
                </Button>
              </Box>
            </Box>

            {/* Menu button - Absolute on mobile to save space */}
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                color: 'text.secondary',
                position: { xs: 'absolute', sm: 'relative' },
                top: { xs: 8, sm: 'auto' },
                right: { xs: 8, sm: 'auto' },
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Snackbar>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{ zIndex: 2100 }}
      >
        <MenuItem 
          onClick={() => handleDismiss(false)}
          sx={{ fontSize: '0.875rem' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ScheduleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Remind me later" 
            secondary="Ask again in 30 days"
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>
        <MenuItem 
          onClick={() => handleDismiss(true)}
          sx={{ fontSize: '0.875rem' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Don't ask again" 
            secondary="Hide this permanently"
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>
        <MenuItem 
          onClick={handleMenuClose}
          sx={{ fontSize: '0.875rem' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Close menu" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default PWAInstallPrompt;